import { useTranslation } from '@hbs-us/ui';
import { createPortal } from 'react-dom';

/**
 * The sheet that comes out of the printer.
 *
 * This is a physical object with a job: it gets taped to a door frame in an unfinished house and a
 * crew scans it on the way in. So it is a poster, not a screenshot of a screen — one big code,
 * enough words to say what scanning does, and the address written out for the phone that will not
 * scan.
 *
 * WHY A PORTAL. It renders into its own `<body>` child rather than in place, and `styles.css`
 * prints that child and hides every other one. Printing it in place cannot work: the app shell is
 * `h-dvh` with `<main>` as the only scroller, and in paged media there is no viewport for `dvh` to
 * be 100% of, so the whole document collapses to one clipped page. Taking the shell out of the
 * layout entirely is what avoids that, and a portal is what makes it a sibling to take out.
 *
 * COLOURS ARE FIXED LIGHT, deliberately, and are the one place on this surface not read from
 * tokens. The token palette follows the account's theme, so a manager on dark mode would print a
 * page of solid ink and a QR most scanners refuse. `white`/`black` utilities are the same escape
 * the on-screen QR already takes (a code must sit on white to be readable at all).
 */

export interface QcQrPrintCardProps {
  /** Data URI produced locally by `qrcode` — no image is ever fetched. */
  qr: string;
  /** The full link the code encodes, printed as text so a phone that will not scan can be typed at. */
  url: string;
  checklistName: string;
  /** Job and template, already composed by the caller. */
  subtitle: string;
}

export function QcQrPrintCard({ qr, url, checklistName, subtitle }: QcQrPrintCardProps) {
  const { t } = useTranslation();

  return createPortal(
    <div className="qc-print-root bg-white text-black">
      <div className="flex flex-col items-center gap-6 py-4 text-center">
        <div className="flex flex-col gap-1">
          <h1 className="font-semibold text-3xl leading-tight">{checklistName}</h1>
          <p className="text-lg">{subtitle}</p>
        </div>

        <p className="max-w-md font-semibold text-xl leading-snug">{t('qc:share.printScanLine')}</p>

        {/*
          Sized in millimetres because the reader is a phone camera at arm's length, not a browser.
          Roughly 70mm square keeps it scannable across a dim room; a `w-64` would mean something
          different on every printer. `p-0` inside a white block preserves the quiet zone the code
          was generated with (margin: 1 module).
        */}
        <img
          src={qr}
          alt={t('qc:share.alt')}
          style={{ width: '70mm', height: '70mm' }}
          className="bg-white"
        />

        <div className="flex max-w-md flex-col gap-2">
          <p className="text-base">{t('qc:share.printFallback')}</p>
          {/* Not `break-all`: a URL broken mid-token is harder to retype correctly than one that
              wraps at its own boundaries. */}
          <p className="break-words font-mono text-sm">{url}</p>
        </div>

        <p className="max-w-md text-sm leading-snug">{t('qc:share.printSignInNote')}</p>
      </div>
    </div>,
    document.body,
  );
}
